<!-- eslint-disable @typescript-eslint/ban-ts-comment -->
<script setup lang="ts">
import { computed, type StyleValue } from "vue";
import { useMatches } from "vue-responsiveness";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";

export interface GridContainerProps {
  columns?: string;
  rows?: string | string[];
  areas?: string | string[];
  area?: string;

  columnsXs?: string;
  rowsXs?: string;
  areasXs?: string | string[];
  areaXs?: string;

  gap?: number | string;
  gapXs?: number | string;

  align?: "center" | "start" | "end";
  justify?: "center" | "start" | "end" | "normal" | "space-around" | "space-between";
}

const props = withDefaults(defineProps<GridContainerProps>(), {
  align: "center",
  justify: "normal",
});
const display = useMatches();

const parseAreas = (area?: string | string[]) => {
  if (!hasValue(area)) return undefined;
  if (!Array.isArray(area)) return area;

  return (area as string[]).reduce((acu, current) => {
    if (!isValidString(acu)) return `'${current}'`;

    return `${acu} '${current}'`;
  }, "");
};

const parseRows = (area?: string | string[]) => {
  if (!hasValue(area)) return undefined;
  if (!Array.isArray(area)) return area;

  return (area as string[]).reduce((acu, current) => {
    if (!isValidString(acu)) return `${current}`;

    return `${acu} ${current}`;
  }, "");
};

const isMobile = computed(() => display.isMax("sm"));

const gap = computed(() => props.gap ?? props.gapXs ?? 4);
const gapXs = computed(() => props.gapXs ?? props.gap ?? 4);

const classes = computed(() => {
  const res: string[] = ["grid-container"];

  if (isMobile.value) {
    res.push(`gap-${gapXs.value}`);
  } else {
    res.push(`gap-${gap.value}`);
  }

  if (hasValue(area.value)) {
    res.push(`grid-column`);
  }

  return res.join(" ");
});

const areas = computed(() => {
  if (isMobile.value) return props.areasXs ?? props.areas;

  return props.areas ?? props.areasXs;
});

const area = computed(() => {
  if (isMobile.value) return props.areaXs ?? props.area;

  return props.area ?? props.areaXs;
});

const columns = computed(() => {
  if (isMobile.value) return props.columnsXs ?? props.columns;

  return props.columns ?? props.columnsXs;
});

const rows = computed(() => {
  if (isMobile.value) return props.rowsXs ?? props.rows;

  return props.rows ?? props.rowsXs;
});

const styles = computed(() => {
  const styles: StyleValue = {
    alignContent: props.align,
    justifyContent: props.justify,
  };

  if (hasValue(areas.value)) {
    styles["gridTemplateAreas"] = `'${parseAreas(areas.value)}'`.replace(/''/gi, "'");
  }

  if (hasValue(area.value)) {
    styles["gridArea"] = area.value;
  }

  if (hasValue(columns)) {
    styles["gridTemplateColumns"] = `${columns.value}`;
  }

  if (hasValue(rows)) {
    styles["gridTemplateRows"] = parseRows(props.rows);
  }

  return styles;
});
</script>

<template>
  <div :style="styles" :class="classes">
    <slot />
  </div>
</template>

<style scoped lang="scss">
.grid-container {
  display: grid;
  width: 100%;
}
</style>
